<template>
  <component
    :is="computedTag"
    :type="isButton ? type : undefined"
    :href="isLink ? href : undefined"
    :target="isLink ? target : undefined"
    :rel="isLink ? rel : undefined"
    :disabled="isButton ? disabled || loading : undefined"
    :class="buttonClasses"
    class="button-v2"
    v-on="$listeners"
  >
    <spinner v-if="loading" size="small" :color-scheme="spinnerScheme" />
    <v2-icon
      v-if="!loading && iconLeft"
      :icon="iconLeft"
      :size="iconSize"
      class="button-v2__icon"
    />
    <v2-icon
      v-if="!loading && icon && !iconLeft && !iconRight && !$slots.default"
      :icon="icon"
      :size="iconSize"
      class="button-v2__icon"
    />
    <span v-if="$slots.default && !isIconOnly" class="button-v2__content">
      <slot />
    </span>
    <v2-icon
      v-if="!loading && iconRight"
      :icon="iconRight"
      :size="iconSize"
      class="button-v2__icon"
    />
  </component>
</template>

<script>
import V2Icon from 'v2/components/Icon.vue';
import Spinner from 'shared/components/Spinner.vue';

export default {
  name: 'ButtonV2',
  components: { V2Icon, Spinner },
  props: {
    variant: {
      type: String,
      default: 'filled',
      validator: v => ['filled', 'outline', 'naked', 'ghost'].includes(v),
    },
    size: {
      type: String,
      default: 'sm',
      validator: v => ['sm', 'md', 'lg'].includes(v),
    },
    colorScheme: {
      type: String,
      default: 'default',
      validator: v => ['default', 'danger'].includes(v),
    },
    icon: {
      type: String,
      default: '',
    },
    iconLeft: {
      type: String,
      default: '',
    },
    iconRight: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'button',
    },
    href: {
      type: String,
      default: '',
    },
    target: {
      type: String,
      default: '_self',
    },
    rel: {
      type: String,
      default: '',
    },
    tag: {
      type: String,
      default: '',
    },
    classNames: {
      type: [String, Object, Array],
      default: '',
    },
  },

  computed: {
    computedTag() {
      if (this.tag) return this.tag;
      if (this.href) return 'a';
      return 'button';
    },

    isButton() {
      return this.computedTag === 'button';
    },

    isLink() {
      return this.computedTag === 'a';
    },

    isIconOnly() {
      return (
        !this.$slots.default && (this.icon || this.iconLeft || this.iconRight)
      );
    },

    spinnerScheme() {
      if (this.variant === 'filled') return '';
      return 'dark';
    },

    iconSize() {
      return this.size === 'sm' ? 16 : 24;
    },

    buttonClasses() {
      return [
        `button-v2--${this.variant}`,
        `button-v2--${this.size}`,
        {
          'button-v2--icon-only': this.isIconOnly,
          'button-v2--full-width': this.fullWidth,
          'button-v2--loading': this.loading,
          'button-v2--danger': this.colorScheme === 'danger',
          'button-v2--disabled': this.disabled || this.loading,
        },
        this.classNames,
      ];
    },
  },
};
</script>

<style scoped lang="scss">
.button-v2 {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border: none;
  cursor: pointer;
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
  font-style: normal;
  text-align: center;
  text-decoration: none;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease;
  box-sizing: border-box;
  white-space: nowrap;
  line-height: 1.25;
  outline: none;
  position: relative;

  &:focus-visible {
    box-shadow:
      0 0 0 2px theme('colors.white'),
      0 0 0 4px theme('colors.brand.DEFAULT');
  }

  &--lg {
    padding: 12px 20px;
    font-size: 16px;
    border-radius: 5px;
    min-height: 48px;
  }

  &--md {
    padding: 7px 11px;
    font-size: 14px;
    border-radius: 5px;
    min-height: 40px;
  }

  &--sm {
    padding: 6px 12px;
    font-size: 14px;
    line-height: 20px;
    border-radius: 5px;
    min-height: 32px;
  }

  &--outline.button-v2--sm,
  &--ghost.button-v2--sm {
    padding: 5px 11px;
  }

  &--outline.button-v2--lg,
  &--ghost.button-v2--lg {
    padding: 11px 19px;
  }

  &--icon-only {
    &.button-v2--lg {
      width: 48px;
      height: 48px;
      padding: 0;
    }
    &.button-v2--md {
      width: 44px;
      height: 44px;
      padding: 0;
    }
    &.button-v2--sm {
      width: 32px;
      height: 32px;
      padding: 0;
    }
  }

  &--full-width {
    width: 100%;
  }

  &--loading {
    cursor: wait;
  }

  &--filled {
    background-color: theme('colors.brand.DEFAULT');
    color: theme('colors.white');

    &:hover:not(.button-v2--disabled) {
      background-color: theme('colors.brand.600');
    }

    &:active:not(.button-v2--disabled) {
      background-color: theme('colors.brand.600');
    }

    &.button-v2--disabled {
      background-color: theme('colors.gray.200');
      color: theme('colors.gray.400');
      cursor: not-allowed;
    }

    &.button-v2--danger {
      background-color: theme('colors.red.500');
      color: theme('colors.white');

      &:hover:not(.button-v2--disabled) {
        background-color: theme('colors.red.600');
      }

      &:active:not(.button-v2--disabled) {
        background-color: theme('colors.red.600');
      }

      &.button-v2--disabled {
        background-color: theme('colors.gray.200');
        color: theme('colors.gray.400');
      }
    }
  }

  &--outline {
    background-color: transparent;
    border: 1px solid theme('colors.brand.DEFAULT');
    color: theme('colors.brand.DEFAULT');

    &:hover:not(.button-v2--disabled) {
      background-color: theme('colors.green.50');
      border-color: theme('colors.brand.600');
      color: theme('colors.brand.600');
    }

    &:active:not(.button-v2--disabled) {
      background-color: theme('colors.green.50');
      border-color: theme('colors.brand.600');
      color: theme('colors.brand.600');
    }

    &.button-v2--disabled {
      background-color: theme('colors.white');
      border-color: theme('colors.gray.200');
      color: theme('colors.gray.400');
      cursor: not-allowed;
    }

    &.button-v2--danger {
      border-color: theme('colors.red.600');
      color: theme('colors.red.600');

      &:hover:not(.button-v2--disabled) {
        background-color: theme('colors.red.50');
        border-color: theme('colors.red.700');
        color: theme('colors.red.700');
      }

      &:active:not(.button-v2--disabled) {
        background-color: theme('colors.red.50');
        border-color: theme('colors.red.700');
        color: theme('colors.red.700');
      }

      &.button-v2--disabled {
        background-color: theme('colors.white');
        border-color: theme('colors.gray.200');
        color: theme('colors.gray.400');
      }
    }
  }

  &--naked {
    background-color: transparent;
    color: theme('colors.gray.700');

    &:hover:not(.button-v2--disabled) {
      background-color: theme('colors.gray.150');
      color: theme('colors.gray.700');
    }

    &:active:not(.button-v2--disabled) {
      background-color: theme('colors.gray.150');
      color: theme('colors.gray.700');
    }

    &.button-v2--disabled {
      color: theme('colors.gray.400');
      cursor: not-allowed;
    }

    &.button-v2--danger {
      color: theme('colors.red.600');

      &:hover:not(.button-v2--disabled) {
        background-color: theme('colors.red.50');
        color: theme('colors.red.700');
      }

      &:active:not(.button-v2--disabled) {
        background-color: theme('colors.red.50');
        color: theme('colors.red.700');
      }

      &.button-v2--disabled {
        color: theme('colors.gray.400');
      }
    }
  }

  &--ghost {
    background-color: theme('colors.white');
    border: 1px solid theme('colors.gray.200');
    color: theme('colors.gray.500');
    border-radius: 4px;
    box-shadow: 0 1px 8px 0 rgba(221, 221, 221, 0.25);

    &:hover:not(.button-v2--disabled) {
      background-color: theme('colors.gray.50');
    }

    &:active:not(.button-v2--disabled) {
      background-color: theme('colors.gray.50');
    }

    &.button-v2--disabled {
      border-color: theme('colors.neutral.200');
      color: theme('colors.neutral.400');
      background-color: theme('colors.neutral.50');
      cursor: not-allowed;
      box-shadow: none;
    }

    &.button-v2--lg,
    &.button-v2--md,
    &.button-v2--sm {
      border-radius: 4px;
    }

    &.button-v2--danger {
      border-color: theme('colors.gray.200');
      color: theme('colors.red.500');

      &:hover:not(.button-v2--disabled) {
        background-color: theme('colors.red.50');
        border-color: theme('colors.red.200');
      }

      &:active:not(.button-v2--disabled) {
        background-color: theme('colors.red.50');
        border-color: theme('colors.red.200');
      }

      &.button-v2--disabled {
        border-color: theme('colors.neutral.200');
        color: theme('colors.neutral.400');
        background-color: theme('colors.neutral.50');
      }
    }
  }
}

[dir='rtl'] .button-v2 {
  .button-v2__icon + .button-v2__content {
    margin-right: 0;
    margin-left: 0;
  }
}
</style>
